import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { createTicket, getUserProfile } from "services/api/user.service";
import { useArtist } from "../../contexts/ArtistContext";

const ZendeskTicketService: React.FC = () => {
  const { currentArtistId, availableArtists } = useArtist();
  const selectedArtist = availableArtists.find((a) => a.artistId === currentArtistId);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState({
    subject: "",
    description: "",
    name: "",
    email: "",
    general: "",
  });

  useEffect(() => {
    let cancelled = false;
    const artistName = selectedArtist?.artistName || "";

    if (!currentArtistId) {
      setName("");
      setEmail("");
      return;
    }

    if (artistName) setName(artistName);

    getUserProfile()
      .then((profile) => {
        if (cancelled) return;
        const ownerName = [profile.firstname, profile.lastname].filter(Boolean).join(" ").trim();
        const profileArtistName = profile.artist?.[0]?.artist_name || "";
        setName(ownerName || profileArtistName || artistName);
        setEmail(profile.email || "");
      })
      .catch(() => {
        if (cancelled) return;
        setEmail("");
        if (artistName) setName(artistName);
      });

    return () => { cancelled = true; };
  }, [currentArtistId, selectedArtist?.artistName]);

  const validate = () => {
    const newErrors = { subject: "", description: "", name: "", email: "", general: "" };
    let valid = true;
    if (!name.trim()) { newErrors.name = "Name is required"; valid = false; }
    if (!email.trim()) { newErrors.email = "Email is required"; valid = false; }
    if (!subject.trim()) { newErrors.subject = "Subject is required"; valid = false; }
    if (!description.trim()) { newErrors.description = "Description is required"; valid = false; }
    setError(newErrors);
    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setError(prev => ({ ...prev, general: "" }));

    try {
      const data = await createTicket({ name, email, subject, description });
      toast.success(`Ticket #${data.ticketId} created successfully`);
      setSubject('');
      setDescription('');
    } catch (err: any) {
      console.error(err);
      setError(prev => ({ ...prev, general: err.message }));
      toast.error(err.message || 'Error submitting ticket');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-nova-dashboard-900 min-h-screen">
      {/* Main Content Area */}
      <div className="p-4 sm:p-6 md:p-8">
        {/* Header */}
        <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern">Support Ticket</span>

        {/* Support Ticket Section */}
        <div className="mb-6 sm:mb-8 mt-[5rem]">
          <span className="text-[1.4375rem] font-regular text-gray-900 mb-6 block font-family-messina-modern">Submit a Support Request</span>
          
          <div className="space-y-4 sm:space-y-6">
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              {/* Name and Email Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {/* Name */}
                <div>
                  <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                    NAME
                  </label>
                  <input
                    type="text"
                    value={name}
                    placeholder="Enter your name"
                    onChange={(e) => setName(e.target.value)}
                    className={`w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b pb-2 focus:outline-none font-family-messina-sans ${
                      error.name ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gray-900'
                    }`}
                  />
                  {error.name && (
                    <p className="text-red-500 text-xs mt-1">{error.name}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                    EMAIL
                  </label>
                  <input
                    type="email"
                    value={email}
                    placeholder="Enter your email"
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b pb-2 focus:outline-none font-family-messina-sans ${
                      error.email ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gray-900'
                    }`}
                  />
                  {error.email && (
                    <p className="text-red-500 text-xs mt-1">{error.email}</p>
                  )}
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                  SUBJECT
                </label>
                <input
                  type="text"
                  value={subject}
                  placeholder="Enter ticket subject"
                  onChange={(e) => setSubject(e.target.value)}
                  className={`w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b pb-2 focus:outline-none font-family-messina-sans ${
                    error.subject ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gray-900'
                  }`}
                />
                {error.subject && (
                  <p className="text-red-500 text-xs mt-1">{error.subject}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                  DESCRIPTION
                </label>
                <textarea
                  value={description}
                  placeholder="Describe your issue or question in detail..."
                  onChange={e => setDescription(e.target.value)}
                  className={`w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b pb-2 focus:outline-none resize-none font-family-messina-sans ${
                    error.description ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gray-900'
                  }`}
                />
                {error.description && (
                  <p className="text-red-500 text-xs mt-1">{error.description}</p>
                )}
              </div>

              {/* General Error */}
              {error.general && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-red-600 text-sm">{error.general}</p>
                </div>
              )}

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-black text-white text-[0.625rem] px-8 py-3 rounded-lg-settings font-medium hover:bg-gray-800 transition-all duration-500 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'SUBMIT TICKET'}
                </button>
              </div>

              {/* Discord Community Section */}
              <div className="pt-6">
                {/* Separator */}
                <div className="flex items-center mb-6">
                  <div className="flex-1 h-px bg-gray-300"></div>
                  <span className="px-4 text-gray-500 text-sm font-family-messina-sans">OR</span>
                  <div className="flex-1 h-px bg-gray-300"></div>
                </div>

                {/* Discord Community Card */}
                <div className="rounded-lg p-3 sm:p-6">
                  <div className="flex items-start gap-3 sm:gap-4">
                    {/* Community Icon */}
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" fill="white"/>
                      </svg>
                    </div>

                    {/* Text Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <span className="text-gray-600 text-base sm:text-lg md:text-[1.625rem] leading-5 sm:leading-6 md:leading-8 sidebar-menu-item break-words">
                          Come help us build Nova!
                        </span>

                        {/* Discord Button */}
                        <a
                          href={process.env.REACT_APP_NOVA_DISCARD_LINK} 
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-black text-white text-[0.625rem] px-5 sm:px-8 py-3 rounded-lg-settings font-medium hover:bg-gray-800 transition-all duration-500 ease-in-out inline-flex items-center justify-center whitespace-nowrap w-full sm:w-auto"
                        >
                          JOIN US ON DISCORD
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZendeskTicketService;
